import React from 'react';
import {
  Box,
  Button,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  formatRequestTypeLabel,
  isDetailsColumn,
  isStatusColumn,
  normalizeDisplayValue,
} from './requestsUiUtils';

function getPrimaryStatusValue(row, columns) {
  const firstStatus = columns.find((col) => isStatusColumn(col));
  if (firstStatus) {
    try {
      return firstStatus.accessor(row);
    } catch {
      return '';
    }
  }
  return row?.status || row?.hr_status || row?.pre_approval_status || '';
}

export default function RequestsMobileCards({
  loading,
  rows,
  columns,
  showActions,
  onRowClick,
  renderRowActions,
  renderStatus,
  onEmptyAction,
  emptyActionLabel,
}) {
  const theme = useTheme();

  if (loading) {
    return (
      <Stack spacing={1.1} sx={{ p: 1.2 }}>
        {Array.from({ length: 4 }).map((_, idx) => (
          <Box
            key={`loading-${idx}`}
            sx={{
              p: 1.4,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.12),
              backgroundColor: alpha(theme.palette.background.paper, 0.94),
            }}
          >
            <Skeleton width="65%" />
            <Skeleton width="35%" />
            <Skeleton sx={{ mt: 1 }} />
            <Skeleton />
            <Skeleton width="45%" />
          </Box>
        ))}
      </Stack>
    );
  }

  if (!rows.length) {
    return (
      <Stack spacing={1.2} alignItems="center" sx={{ p: 3 }}>
        <Typography sx={{ opacity: 0.74 }}>No requests found</Typography>
        {onEmptyAction ? (
          <Button variant="outlined" onClick={onEmptyAction}>
            {emptyActionLabel || 'Create a new request'}
          </Button>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack spacing={1.1} sx={{ p: 1.1 }}>
      {rows.map((row) => {
        const primaryStatus = getPrimaryStatusValue(row, columns);

        return (
          <Box
            key={row.id}
            onClick={() => onRowClick(row)}
            sx={{
              borderRadius: 2.8,
              p: 1.45,
              cursor: 'pointer',
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, 0.16),
              backgroundColor: alpha(theme.palette.background.paper, 0.98),
              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease, background-color 160ms ease',
              boxShadow: `0 8px 28px ${alpha(theme.palette.common.black, 0.04)}`,
              '&:active': { transform: 'scale(0.996)' },
              '&:hover': {
                borderColor: alpha(theme.palette.primary.main, 0.24),
                backgroundColor: alpha(theme.palette.primary.main, 0.018),
              },
            }}
          >
            <Stack direction="row" spacing={1.2} alignItems="flex-start" justifyContent="space-between">
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase' }}
                >
                  {formatRequestTypeLabel(row?.request_type)}
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 820, mt: 0.25 }} noWrap>
                  {normalizeDisplayValue(row?.user?.name)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Request #{row?.id || '-'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={0.7} alignItems="center" sx={{ flexShrink: 0 }}>
                {primaryStatus ? renderStatus(primaryStatus) : null}
              </Stack>
            </Stack>

            <Divider sx={{ my: 1.2 }} />

            <Stack spacing={0.85}>
              {columns.slice(0, 5).map((col, idx) => {
                let value = '';
                try {
                  value = col.accessor(row);
                } catch {
                  value = '';
                }

                if (isStatusColumn(col)) return null;

                if (isDetailsColumn(col)) {
                  const details = String(value || '').trim();
                  const shortText =
                    details.length > 110 ? `${details.slice(0, 110)}...` : details || '-';
                  return (
                    <Box key={`${col.label}-${idx}`}>
                      <Typography variant="caption" color="text.secondary">
                        {col.label}
                      </Typography>
                      {details ? (
                        <Tooltip title={details}>
                          <Typography variant="body2">{shortText}</Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2">-</Typography>
                      )}
                    </Box>
                  );
                }

                return (
                  <Stack
                    key={`${col.label}-${idx}`}
                    direction="row"
                    spacing={1}
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="caption" color="text.secondary">
                      {col.label}
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'right', ml: 1 }}>
                      {normalizeDisplayValue(value)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={0.8}
              sx={{ mt: 1.35 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Button size="small" variant="text" sx={{ alignSelf: 'flex-start', px: 0 }} onClick={() => onRowClick(row)}>
                View details
              </Button>
              {showActions ? renderRowActions(row) : null}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}
